import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Container, Form, Modal, Spinner, Table } from "react-bootstrap";
import API from "../../services/api";
import { fmtDate, orDash } from "./AccountsShared";
import "../EmployeeAccounts.css";

const blankForm = {
  partyType: "customer",
  ledgerName: "",
  companyName: "",
  legalName: "",
  tradeName: "",
  contactName: "",
  mobile: "",
  email: "",
  gstNumber: "",
  panNumber: "",
  gstState: "",
  constitution: "",
  registrationType: "Regular",
  registrationStatus: "",
  registrationDate: "",
  cancellationDate: "",
  taxpayerType: "",
  stateJurisdiction: "",
  centreJurisdiction: "",
  stateJurisdictionCode: "",
  centreJurisdictionCode: "",
  natureOfBusiness: [],
  einvoiceStatus: "",
  lastGstUpdatedDate: "",
  filingFrequency: "Monthly",
  gstTurnover: "",
  gstTurnoverSlab: "",
  gstTurnoverFy: "",
  gstTurnoverSource: "",
  address: "",
  city: "",
  state: "",
  pincode: "",
  openingBalance: "",
  balanceType: "dr",
  creditLimit: "",
  paymentTermsDays: "",
  notes: "",
};

const fmtMoney = (value) =>
  `₹ ${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const isTurnoverUnavailable = (source = "", total = 0) =>
  Number(total || 0) === 0 && /not provided|not available|not disclosed/i.test(String(source || ""));

const statusLabel = (status = "") =>
  String(status || "pending")
    .replace(/^nil$/i, "Nil")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());

const asCsv = (value) => (Array.isArray(value) ? value.join(", ") : value || "");
const totalTurnover = (rows = []) =>
  rows.reduce((sum, row) => sum + Number(row.turnover || 0), 0);
const normText = (value = "") => String(value || "").trim().toLowerCase();
const normUpper = (value = "") => String(value || "").trim().toUpperCase();

const findDuplicateParty = (rows = [], payload = {}, editingId = "") => {
  const gstNumber = normUpper(payload.gstNumber);
  const panNumber = normUpper(payload.panNumber);
  const companyName = normText(payload.companyName);
  const ledgerName = normText(payload.ledgerName);
  const mobile = String(payload.mobile || "").replace(/\D/g, "").trim();

  return rows.find((row) => {
    if (editingId && row?._id === editingId) return false;
    const rowGstNumber = normUpper(row?.gstNumber);
    const rowPanNumber = normUpper(row?.panNumber);
    const rowCompanyName = normText(row?.companyName);
    const rowLedgerName = normText(row?.ledgerName);
    const rowMobile = String(row?.mobile || "").replace(/\D/g, "").trim();

    return (
      (gstNumber && rowGstNumber === gstNumber) ||
      (panNumber && rowPanNumber === panNumber) ||
      (companyName && ledgerName && rowCompanyName === companyName && rowLedgerName === ledgerName) ||
      (companyName && mobile && rowCompanyName === companyName && rowMobile === mobile)
    );
  });
};

const duplicatePartyMessage = (party = {}) => {
  const ref = party.gstNumber || party.panNumber || party.mobile || party.ledgerName || "same details";
  return `Duplicate party ledger already exists for ${party.companyName || party.ledgerName || "this party"} (${ref}).`;
};

const filingDetail = (row = {}) => {
  const returns = Array.isArray(row.gstReturns) ? row.gstReturns : [];
  if (returns.length) {
    return returns
      .map((item) => `${item.returnType || "Return"} ${item.status || ""}`.trim())
      .join(", ");
  }
  if (row.status === "nil") return "Nil return / no bills";
  return `${row.invoiceCount || 0} bills`;
};

export const AccountsParties = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [err, setErr] = useState("");
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [editingId, setEditingId] = useState("");
  const [lookup, setLookup] = useState(null);
  const [ledgerModal, setLedgerModal] = useState({ show: false, loading: false, data: null });

  const fetchRows = async () => {
    try {
      setLoading(true);
      setErr("");
      const res = await API.get("/employee/accounts/parties");
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load parties");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) =>
      [
        row.ledgerName,
        row.companyName,
        row.gstNumber,
        row.panNumber,
        row.mobile,
        row.gstState,
        row.partyType,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [rows, search]);

  const summary = useMemo(() => {
    const active = rows.filter((row) => row.status !== "inactive").length;
    const gst = rows.filter((row) => row.gstNumber).length;
    const opening = rows.reduce((sum, row) => {
      const amount = Number(row.openingBalance || 0);
      return sum + (row.balanceType === "cr" ? -amount : amount);
    }, 0);
    return { active, gst, opening };
  }, [rows]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const openCreate = () => {
    setEditingId("");
    setForm(blankForm);
    setLookup(null);
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditingId(row._id);
    setForm({ ...blankForm, ...row, openingBalance: row.openingBalance || "", creditLimit: row.creditLimit || "" });
    setLookup(null);
    setShowForm(true);
  };

  const runGstLookup = async () => {
    const gstNumber = String(form.gstNumber || "").trim().toUpperCase();
    if (!gstNumber) {
      setErr("GST number enter kijiye.");
      return;
    }
    try {
      setLookupLoading(true);
      setErr("");
      const res = await API.get("/employee/accounts/gst-lookup", { params: { gstNumber } });
      const data = res.data || {};
      setLookup(data);
      setForm((prev) => ({
        ...prev,
        gstNumber: data.gstNumber || prev.gstNumber,
        panNumber: data.panNumber || prev.panNumber,
        gstState: data.state || prev.gstState,
        state: data.state || prev.state,
        constitution: data.constitution || prev.constitution,
        registrationType: data.registrationType || prev.registrationType,
        registrationStatus: data.registrationStatus || prev.registrationStatus,
        registrationDate: data.registrationDate || prev.registrationDate,
        cancellationDate: data.cancellationDate || prev.cancellationDate,
        taxpayerType: data.taxpayerType || prev.taxpayerType,
        legalName: data.legalName || prev.legalName,
        tradeName: data.tradeName || prev.tradeName,
        stateJurisdiction: data.stateJurisdiction || prev.stateJurisdiction,
        centreJurisdiction: data.centreJurisdiction || prev.centreJurisdiction,
        stateJurisdictionCode: data.stateJurisdictionCode || prev.stateJurisdictionCode,
        centreJurisdictionCode: data.centreJurisdictionCode || prev.centreJurisdictionCode,
        natureOfBusiness: data.natureOfBusiness || prev.natureOfBusiness,
        einvoiceStatus: data.einvoiceStatus || prev.einvoiceStatus,
        lastGstUpdatedDate: data.lastGstUpdatedDate || prev.lastGstUpdatedDate,
        filingFrequency: data.filingFrequency || prev.filingFrequency,
        gstTurnover: data.gstTurnover || prev.gstTurnover,
        gstTurnoverSlab: data.gstTurnoverSlab || prev.gstTurnoverSlab,
        gstTurnoverFy: data.gstTurnoverFy || prev.gstTurnoverFy,
        gstTurnoverSource: data.gstTurnoverSource || prev.gstTurnoverSource,
        companyName: data.companyName || prev.companyName,
        ledgerName: data.ledgerName || prev.ledgerName || data.companyName || "",
        contactName: data.contactName || prev.contactName,
        mobile: data.mobile || prev.mobile,
        email: data.email || prev.email,
        address: data.address || prev.address,
        city: data.city || prev.city,
        pincode: data.pincode || prev.pincode,
      }));
      setMessage(
        data.provider === "sandbox" || data.provider === "rapidapi"
          ? "GST portal details loaded from public GST API."
          : data.providerError
            ? `GST fallback used: ${data.providerError}`
            : data.found
              ? "GST matched with existing records."
              : data.providerConfigured === false
                ? "GST API key configured nahi hai. Abhi GSTIN se PAN/state/entity type hi decode hoga."
                : "GST details decoded. GST provider key configure hone par live portal details bhi pull hongi."
      );
    } catch (e) {
      const status = e?.response?.status;
      const serverMessage = e?.response?.data?.message || e?.response?.data?.error;
      setErr(
        serverMessage ||
          (status ? `GST lookup failed (${status})` : "GST lookup failed. Backend/API connection check kijiye.")
      );
    } finally {
      setLookupLoading(false);
    }
  };

  const submitForm = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      gstNumber: String(form.gstNumber || "").trim().toUpperCase(),
      panNumber: String(form.panNumber || "").trim().toUpperCase(),
      mobile: String(form.mobile || "").replace(/\D/g, "").trim(),
      ledgerName: String(form.ledgerName || form.companyName || "").trim(),
      companyName: String(form.companyName || form.ledgerName || "").trim(),
      natureOfBusiness: Array.isArray(form.natureOfBusiness)
        ? form.natureOfBusiness
        : String(form.natureOfBusiness || "").split(",").map((item) => item.trim()).filter(Boolean),
    };
    const duplicate = findDuplicateParty(rows, payload, editingId);
    if (duplicate) {
      setErr(duplicatePartyMessage(duplicate));
      return;
    }

    try {
      setSaving(true);
      setErr("");
      if (editingId) await API.put(`/employee/accounts/parties/${editingId}`, payload);
      else await API.post("/employee/accounts/parties", payload);
      setShowForm(false);
      setMessage(editingId ? "Party ledger updated." : "Party ledger created.");
      fetchRows();
    } catch (e1) {
      setErr(e1?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const openLedger = async (row) => {
    setLedgerModal({ show: true, loading: true, data: null });
    try {
      const res = await API.get(`/employee/accounts/parties/${row._id}/ledger`);
      setLedgerModal({ show: true, loading: false, data: res.data });
    } catch (e) {
      setLedgerModal({ show: true, loading: false, data: { error: e?.response?.data?.message || "Ledger load failed" } });
    }
  };

  const filingRows = lookup?.filingSummary || ledgerModal.data?.filingSummary || [];
  const visibleTurnoverTotal = totalTurnover(filingRows);
  const turnoverUnavailable = isTurnoverUnavailable(form.gstTurnoverSource, visibleTurnoverTotal);

  return (
    <Container className="accounts-page accounts-module-page accounts-party-page">
      <section className="accounts-module-shell">
        <header className="accounts-module-header">
          <div className="accounts-module-title">
            <div className="accounts-module-icon">
              <span className="material-symbols-outlined">account_balance_wallet</span>
            </div>
            <div>
              <h2>Party Ledger</h2>
              <p>Party create, GST auto-fill, ledger balance, aur 12 month GST filing view.</p>
            </div>
          </div>
          <div className="accounts-module-header-actions">
            <Button variant="outline-secondary" onClick={fetchRows}>Refresh</Button>
            <Button className="accounts-btn-primary" onClick={openCreate}>Create Party</Button>
          </div>
        </header>

        <section className="accounts-module-summary">
          <article><span>Total Parties</span><strong>{rows.length}</strong></article>
          <article><span>Active Ledgers</span><strong>{summary.active}</strong></article>
          <article><span>GST Linked</span><strong>{summary.gst}</strong></article>
          <article><span>Opening Net</span><strong>{fmtMoney(summary.opening)}</strong></article>
        </section>

        <section className="accounts-module-filters">
          <label className="accounts-module-search">
            <span className="material-symbols-outlined">search</span>
            <input placeholder="Search party, GST, PAN, mobile..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </label>
        </section>

        {err && <Alert variant="danger" className="mx-4 mb-0">{err}</Alert>}
        {message && <Alert variant="success" className="mx-4 mb-0">{message}</Alert>}

        <section className="accounts-module-tablecard">
          {loading ? (
            <div className="accounts-module-loading"><Spinner animation="border" /></div>
          ) : (
            <div className="accounts-module-tablewrap">
              <Table responsive className="accounts-module-table">
                <thead>
                  <tr>
                    <th>Ledger</th>
                    <th>GST / PAN</th>
                    <th>Type</th>
                    <th>State</th>
                    <th>Opening</th>
                    <th>Status</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0 ? (
                    <tr><td colSpan="7" className="text-center py-5 text-muted">No party ledger found</td></tr>
                  ) : filteredRows.map((row) => (
                    <tr key={row._id}>
                      <td><strong>{row.ledgerName}</strong><div className="text-muted small">{orDash(row.companyName)} | {orDash(row.mobile)}</div></td>
                      <td><span className="accounts-mono">{orDash(row.gstNumber)}</span><div className="text-muted small">{orDash(row.panNumber)}</div></td>
                      <td>{statusLabel(row.partyType)}</td>
                      <td>{orDash(row.gstState || row.state)}</td>
                      <td>{fmtMoney(row.openingBalance)} {String(row.balanceType || "dr").toUpperCase()}</td>
                      <td><span className={`accounts-status-pill ${row.status}`}>{statusLabel(row.status)}</span></td>
                      <td className="text-end">
                        <div className="accounts-module-actions">
                          <button type="button" className="approve" onClick={() => openLedger(row)}>Ledger</button>
                          <button type="button" onClick={() => openEdit(row)}>Edit</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </section>
      </section>

      <Modal show={showForm} onHide={() => setShowForm(false)} size="xl" scrollable dialogClassName="accounts-party-modal">
        <Modal.Header closeButton className="accounts-party-modal-head">
          <Modal.Title>
            <span className="material-symbols-outlined">account_balance</span>
            <span>{editingId ? "Edit Party Ledger" : "Create Party Ledger"}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={submitForm} className="accounts-party-form">
            {lookup ? (
              <Alert variant={lookup.provider === "sandbox" ? "success" : "warning"}>
                {lookup.provider === "sandbox" || lookup.provider === "rapidapi"
                  ? "Live GST portal details loaded."
                  : lookup.providerConfigured === false
                    ? "Live GST API key configured nahi hai, isliye portal ke legal name/address/status empty rahenge."
                    : lookup.providerError || "Live GST details nahi aaye, fallback values use ho rahi hain."}
              </Alert>
            ) : null}
            <section className="accounts-party-lookup-card">
              <div>
                <span className="accounts-party-step">GST Auto Fill</span>
                <h4>GSTIN se ledger details pull karein</h4>
                <p>GST number enter karke legal name, address, available mobile/email, turnover aur filing info auto-fill karein.</p>
              </div>
              <div className="accounts-gst-inline">
                <Form.Control value={form.gstNumber} onChange={(e) => setField("gstNumber", e.target.value.toUpperCase())} placeholder="27ABCDE1234F1Z5" />
                <Button type="button" variant="outline-primary" onClick={runGstLookup} disabled={lookupLoading}>
                  {lookupLoading ? "Checking..." : "Auto Fill"}
                </Button>
              </div>
            </section>

            <section className="accounts-party-card">
              <div className="accounts-party-section-head">
                <span className="material-symbols-outlined">business</span>
                <div>
                  <h5>Basic Ledger Details</h5>
                  <p>Ledger name, company identity aur contact person.</p>
                </div>
              </div>
              <div className="accounts-party-form-grid">
                <Form.Group>
                  <Form.Label>Party Type</Form.Label>
                  <Form.Select value={form.partyType} onChange={(e) => setField("partyType", e.target.value)}>
                    <option value="customer">Customer</option>
                    <option value="supplier">Supplier</option>
                    <option value="vendor">Vendor</option>
                    <option value="both">Both</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group><Form.Label>Ledger Name</Form.Label><Form.Control required value={form.ledgerName} onChange={(e) => setField("ledgerName", e.target.value)} placeholder="Ledger display name" /></Form.Group>
                <Form.Group><Form.Label>Company Name</Form.Label><Form.Control required value={form.companyName} onChange={(e) => setField("companyName", e.target.value)} placeholder="Company / firm name" /></Form.Group>
                <Form.Group><Form.Label>Legal Name</Form.Label><Form.Control value={form.legalName} onChange={(e) => setField("legalName", e.target.value)} /></Form.Group>
                <Form.Group><Form.Label>Trade Name</Form.Label><Form.Control value={form.tradeName} onChange={(e) => setField("tradeName", e.target.value)} /></Form.Group>
                <Form.Group><Form.Label>Contact Name</Form.Label><Form.Control value={form.contactName} onChange={(e) => setField("contactName", e.target.value)} /></Form.Group>
                <Form.Group><Form.Label>Mobile</Form.Label><Form.Control value={form.mobile} onChange={(e) => setField("mobile", e.target.value)} /></Form.Group>
                <Form.Group><Form.Label>Email</Form.Label><Form.Control type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} /></Form.Group>
                <Form.Group><Form.Label>PAN</Form.Label><Form.Control value={form.panNumber} onChange={(e) => setField("panNumber", e.target.value.toUpperCase())} /></Form.Group>
              </div>
            </section>

            <section className="accounts-party-card">
              <div className="accounts-party-section-head">
                <span className="material-symbols-outlined">verified</span>
                <div>
                  <h5>GST & Compliance</h5>
                  <p>Registration, jurisdiction, turnover aur filing details.</p>
                </div>
              </div>
              <div className="accounts-party-form-grid">
                <Form.Group><Form.Label>GST State</Form.Label><Form.Control value={form.gstState} onChange={(e) => setField("gstState", e.target.value)} /></Form.Group>
                <Form.Group><Form.Label>GST Status</Form.Label><Form.Control value={form.registrationStatus} onChange={(e) => setField("registrationStatus", e.target.value)} /></Form.Group>
                <Form.Group><Form.Label>Registration Type</Form.Label><Form.Control value={form.registrationType} onChange={(e) => setField("registrationType", e.target.value)} /></Form.Group>
                <Form.Group><Form.Label>Registration Date</Form.Label><Form.Control value={form.registrationDate} onChange={(e) => setField("registrationDate", e.target.value)} /></Form.Group>
                <Form.Group><Form.Label>Cancellation Date</Form.Label><Form.Control value={form.cancellationDate} onChange={(e) => setField("cancellationDate", e.target.value)} /></Form.Group>
                <Form.Group><Form.Label>Constitution</Form.Label><Form.Control value={form.constitution} onChange={(e) => setField("constitution", e.target.value)} /></Form.Group>
                <Form.Group><Form.Label>Taxpayer Type</Form.Label><Form.Control value={form.taxpayerType} onChange={(e) => setField("taxpayerType", e.target.value)} /></Form.Group>
                <Form.Group><Form.Label>E-Invoice Status</Form.Label><Form.Control value={form.einvoiceStatus} onChange={(e) => setField("einvoiceStatus", e.target.value)} /></Form.Group>
                <Form.Group><Form.Label>GST Last Updated</Form.Label><Form.Control value={form.lastGstUpdatedDate} onChange={(e) => setField("lastGstUpdatedDate", e.target.value)} /></Form.Group>
                <Form.Group><Form.Label>GST Turnover</Form.Label><Form.Control type="number" value={form.gstTurnover} onChange={(e) => setField("gstTurnover", e.target.value)} /></Form.Group>
                <Form.Group><Form.Label>GST Turnover Slab</Form.Label><Form.Control value={form.gstTurnoverSlab} onChange={(e) => setField("gstTurnoverSlab", e.target.value)} /></Form.Group>
                <Form.Group><Form.Label>Turnover FY</Form.Label><Form.Control value={form.gstTurnoverFy} onChange={(e) => setField("gstTurnoverFy", e.target.value)} /></Form.Group>
                <Form.Group><Form.Label>Turnover Source</Form.Label><Form.Control value={form.gstTurnoverSource} onChange={(e) => setField("gstTurnoverSource", e.target.value)} /></Form.Group>
                <Form.Group><Form.Label>Filing Frequency</Form.Label><Form.Select value={form.filingFrequency} onChange={(e) => setField("filingFrequency", e.target.value)}><option>Monthly</option><option>Quarterly</option><option>Composition</option></Form.Select></Form.Group>
                <Form.Group className="accounts-party-span"><Form.Label>Nature of Business</Form.Label><Form.Control value={asCsv(form.natureOfBusiness)} onChange={(e) => setField("natureOfBusiness", e.target.value)} placeholder="Manufacturer, wholesaler, trader..." /></Form.Group>
                <Form.Group className="accounts-party-span"><Form.Label>State Jurisdiction</Form.Label><Form.Control value={form.stateJurisdiction} onChange={(e) => setField("stateJurisdiction", e.target.value)} /></Form.Group>
                <Form.Group><Form.Label>State Jurisdiction Code</Form.Label><Form.Control value={form.stateJurisdictionCode} onChange={(e) => setField("stateJurisdictionCode", e.target.value)} /></Form.Group>
                <Form.Group className="accounts-party-span"><Form.Label>Centre Jurisdiction</Form.Label><Form.Control value={form.centreJurisdiction} onChange={(e) => setField("centreJurisdiction", e.target.value)} /></Form.Group>
                <Form.Group><Form.Label>Centre Jurisdiction Code</Form.Label><Form.Control value={form.centreJurisdictionCode} onChange={(e) => setField("centreJurisdictionCode", e.target.value)} /></Form.Group>
              </div>
            </section>

            <section className="accounts-party-card">
              <div className="accounts-party-section-head">
                <span className="material-symbols-outlined">location_on</span>
                <div>
                  <h5>Address & Ledger Terms</h5>
                  <p>Billing address, opening balance, credit limit aur notes.</p>
                </div>
              </div>
              <div className="accounts-party-form-grid">
                <Form.Group className="accounts-party-span"><Form.Label>Address</Form.Label><Form.Control value={form.address} onChange={(e) => setField("address", e.target.value)} placeholder="Complete billing address" /></Form.Group>
                <Form.Group><Form.Label>City</Form.Label><Form.Control value={form.city} onChange={(e) => setField("city", e.target.value)} /></Form.Group>
                <Form.Group><Form.Label>State</Form.Label><Form.Control value={form.state} onChange={(e) => setField("state", e.target.value)} /></Form.Group>
                <Form.Group><Form.Label>Pincode</Form.Label><Form.Control value={form.pincode} onChange={(e) => setField("pincode", e.target.value)} /></Form.Group>
                <Form.Group><Form.Label>Opening Balance</Form.Label><Form.Control type="number" value={form.openingBalance} onChange={(e) => setField("openingBalance", e.target.value)} /></Form.Group>
                <Form.Group><Form.Label>Balance Type</Form.Label><Form.Select value={form.balanceType} onChange={(e) => setField("balanceType", e.target.value)}><option value="dr">Debit</option><option value="cr">Credit</option></Form.Select></Form.Group>
                <Form.Group><Form.Label>Credit Limit</Form.Label><Form.Control type="number" value={form.creditLimit} onChange={(e) => setField("creditLimit", e.target.value)} /></Form.Group>
                <Form.Group><Form.Label>Payment Terms Days</Form.Label><Form.Control type="number" value={form.paymentTermsDays} onChange={(e) => setField("paymentTermsDays", e.target.value)} /></Form.Group>
                <Form.Group className="accounts-party-span"><Form.Label>Notes</Form.Label><Form.Control as="textarea" rows={2} value={form.notes} onChange={(e) => setField("notes", e.target.value)} placeholder="Internal notes for accounts team" /></Form.Group>
              </div>
            </section>

            {filingRows.length ? (
              <>
              <div className="accounts-turnover-summary">
                <span>12 Month GST Turnover</span>
                <strong>{turnoverUnavailable ? "GST holder has not disclosed turnover" : fmtMoney(visibleTurnoverTotal)}</strong>
                <small>{form.gstTurnoverSource || "Calculated from available filing/invoice data"}</small>
              </div>
              <div className="accounts-filing-strip">
                {filingRows.map((row) => (
                  <div key={row.period} className={`accounts-filing-chip ${row.status}`}>
                    <span>{row.label}</span>
                    <strong>{statusLabel(row.status)}</strong>
                    <small>{filingDetail(row)}</small>
                    <small>{fmtMoney(row.turnover)}</small>
                  </div>
                ))}
              </div>
              </>
            ) : null}

            <div className="accounts-party-form-actions">
              <Button variant="outline-secondary" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" className="accounts-btn-primary" disabled={saving}>{saving ? "Saving..." : "Save Ledger"}</Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={ledgerModal.show} onHide={() => setLedgerModal({ show: false, loading: false, data: null })} size="xl" scrollable>
        <Modal.Header closeButton><Modal.Title>Ledger & GST Filing</Modal.Title></Modal.Header>
        <Modal.Body>
          {ledgerModal.loading ? (
            <div className="accounts-module-loading"><Spinner animation="border" /></div>
          ) : ledgerModal.data?.error ? (
            <Alert variant="danger">{ledgerModal.data.error}</Alert>
          ) : (
            <>
              <div className="accounts-ledger-title">
                <strong>{ledgerModal.data?.party?.ledgerName}</strong>
                <span>{orDash(ledgerModal.data?.party?.gstNumber)}</span>
              </div>
              <div className="accounts-filing-strip">
                {(ledgerModal.data?.filingSummary || []).map((row) => (
                  <div key={row.period} className={`accounts-filing-chip ${row.status}`}>
                    <span>{row.label}</span>
                    <strong>{statusLabel(row.status)}</strong>
                    <small>{filingDetail(row)}</small>
                    <small>{fmtMoney(row.gstAmount)} GST</small>
                  </div>
                ))}
              </div>
              <Table responsive className="accounts-module-table mt-3">
                <thead><tr><th>Date</th><th>Ref</th><th>Type</th><th>Debit</th><th>Credit</th><th>Balance</th><th>Status</th></tr></thead>
                <tbody>
                  {(ledgerModal.data?.ledger || []).map((row, index) => (
                    <tr key={`${row.ref}-${index}`}>
                      <td>{fmtDate(row.date)}</td>
                      <td>{row.ref}</td>
                      <td>{statusLabel(row.type)}</td>
                      <td>{fmtMoney(row.debit)}</td>
                      <td>{fmtMoney(row.credit)}</td>
                      <td><strong>{fmtMoney(row.balance)}</strong></td>
                      <td>{statusLabel(row.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default AccountsParties;
